import React, { useState } from 'react';
import { Alert, Button, Form, Spinner, ListGroup, Badge, Modal } from 'react-bootstrap';
import { Check, Plus, Church, Star } from 'lucide-react';
import { useChurch } from '../../contexts/ChurchContext';
import apiClient from '../../utils/apiClient';

interface NewChurchForm {
  name: string;
  address: string;
}

export const ChurchesSettings: React.FC = () => {
  const { userChurches, currentChurch, switchChurch, refreshChurches, isLoadingChurches } = useChurch();
  const [switching, setSwitching] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Add church modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [newChurch, setNewChurch] = useState<NewChurchForm>({ name: '', address: '' });
  const [adding, setAdding] = useState(false);

  const handleSetCurrent = async (churchId: number) => {
    if (churchId === currentChurch?.id) return;

    try {
      setSwitching(churchId);
      setError(null);
      setSuccess(null);

      await switchChurch(churchId);

      const churchName = userChurches.find(c => c.id === churchId)?.name;
      setSuccess(`Current church set to ${churchName}`);
    } catch (err) {
      console.error('Error switching church:', err);
      setError('Failed to update current church');
    } finally {
      setSwitching(null);
    }
  };

  const handleAddChurch = async () => {
    if (!newChurch.name.trim()) {
      setError('Church name is required');
      return;
    }

    try {
      setAdding(true);
      setError(null);

      await apiClient.post('/churches', {
        name: newChurch.name.trim(),
        address: newChurch.address.trim() || null
      });

      await refreshChurches();

      setSuccess(`Church "${newChurch.name}" added successfully`);
      setShowAddModal(false);
      setNewChurch({ name: '', address: '' });
    } catch (err: any) {
      console.error('Error adding church:', err);
      setError(err.response?.data?.message || 'Failed to add church');
    } finally {
      setAdding(false);
    }
  };

  if (isLoadingChurches) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading churches...
      </div>
    );
  }

  return (
    <div className="churches-settings">
      <p className="text-muted mb-3">
        Manage your churches. The current church is displayed in the navigation bar.
      </p>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          <Check size={16} className="me-2" />
          {success}
        </Alert>
      )}

      <ListGroup className="mb-3">
        {userChurches.length === 0 ? (
          <ListGroup.Item className="text-muted">
            No churches found. Add your first church below.
          </ListGroup.Item>
        ) : (
          userChurches.map(church => (
            <ListGroup.Item
              key={church.id}
              className="d-flex justify-content-between align-items-center"
            >
              <div className="d-flex align-items-center">
                <Church size={18} className="me-2 text-muted" />
                <div>
                  <span className="fw-medium">{church.name}</span>
                  {church.address && (
                    <small className="text-muted d-block">{church.address}</small>
                  )}
                </div>
              </div>
              <div className="d-flex align-items-center gap-2">
                {currentChurch?.id === church.id ? (
                  <Badge bg="primary" className="d-flex align-items-center">
                    <Star size={12} className="me-1" />
                    Current
                  </Badge>
                ) : (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleSetCurrent(church.id)}
                    disabled={switching === church.id}
                  >
                    {switching === church.id ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      'Set as Current'
                    )}
                  </Button>
                )}
              </div>
            </ListGroup.Item>
          ))
        )}
      </ListGroup>

      <Button
        variant="outline-secondary"
        onClick={() => setShowAddModal(true)}
      >
        <Plus size={16} className="me-2" />
        Add Church
      </Button>

      {/* Add Church Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Church</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Church Name *</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., St. Luke's Episcopal Church"
              value={newChurch.name}
              onChange={(e) => setNewChurch({ ...newChurch, name: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Address (optional)</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., 123 Main St, City, State"
              value={newChurch.address}
              onChange={(e) => setNewChurch({ ...newChurch, address: e.target.value })}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAddChurch}
            disabled={adding || !newChurch.name.trim()}
          >
            {adding ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Adding...
              </>
            ) : (
              'Add Church'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
